/**
 * Matrice COPSOQ III d'une campagne : un candidat par ligne, un domaine par
 * colonne, la CLASSE ÉTALONNÉE (1 à 5) en cellule — le chiffre que la fiche
 * résultat d'un candidat affiche en tête de chaque domaine.
 *
 * Même patron que `PamiCampagneTable` : largeurs `auto`, colonne Candidat
 * figée — sur un écran étroit la table défile au lieu d'écraser les dates —,
 * pied moyenne / écart-type recalculé sur les lignes filtrées. Le COPSOQ est
 * un diagnostic collectif : la moyenne de la campagne est souvent la ligne
 * qu'on vient lire.
 */
import {
  Button,
  Card,
  CardContent,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  filtreRecherche,
  type ColonneDataTable,
  type LignePied,
  type SortingState,
} from "@repo/ui";
import { cn, dateCourte, motPluriel, pluriel } from "@repo/utils";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { DOMAINS_CONFIG } from "./copsoq3.constants";
import { classeDomaine, totalBrutDomaine } from "./copsoq3.utils";
import { telechargerCsv } from "./csv";

/** Une passation COPSOQ III, telle que renvoyée par
 *  `obtenirEvaluationsCopsoq3ParCampagne` : somme brute par échelle. */
export type Copsoq3EvalRow = {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  dateFin?: number;
  echelles: { echelle: string; domaine: string; score: number }[];
};

type Props = {
  evaluations: Copsoq3EvalRow[];
  /** Nom de la campagne, pour nommer le fichier exporté. */
  campagneNom?: string;
};

/** Score d'un domaine : somme brute et classe étalonnée qui en découle. */
type ScoreDomaine = { totalBrut: number; classe: number };

/** Une ligne de la matrice : la passation, ses domaines déjà étalonnés. */
type Copsoq3Ligne = Copsoq3EvalRow & {
  domaines: Partial<Record<string, ScoreDomaine>>;
};

/** Ordre par défaut : les passations les plus récentes d'abord. */
const TRI_INITIAL: SortingState = [{ id: "date", desc: true }];

const RECHERCHE = {
  colonne: "candidat",
  placeholder: "Nom, prénom…",
  libelle: "Rechercher un candidat",
} as const;

const BARRE_FILTRES = { recherche: RECHERCHE };

/**
 * Teinte d'une classe. Tous les items COPSOQ III sont recodés pour qu'un score
 * haut soit favorable (« Prenez-vous du retard ? » : Jamais = 4), dans les six
 * domaines : une classe basse est donc toujours un signal de risque. Zones de
 * SÉVÉRITÉ, d'où `--etat-*`, comme les barres de la fiche résultat — 1-2 en
 * risque, 3 à surveiller, 4-5 favorable, symétrique autour de la classe médiane.
 */
function teinteClasse(classe: number): string {
  if (classe >= 4)
    return "bg-etat-fait-surface border-etat-fait-trait text-etat-fait-contenu";
  if (classe === 3)
    return "bg-etat-alerte-surface border-etat-alerte-trait text-etat-alerte-contenu";
  return "bg-etat-erreur-surface border-etat-erreur-trait text-etat-erreur-contenu";
}

function nomCandidat(ligne: Copsoq3EvalRow): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

/**
 * Domaines étalonnés d'une passation. Un domaine dont aucune échelle n'a été
 * répondue reste absent : une classe 1 sur une somme nulle mentirait.
 */
function scoresDomaines(
  evaluation: Copsoq3EvalRow,
): Partial<Record<string, ScoreDomaine>> {
  const scoreParEchelle: Record<string, number> = {};
  const domainesRepondus = new Set<string>();
  for (const e of evaluation.echelles) {
    scoreParEchelle[e.echelle] = e.score;
    domainesRepondus.add(e.domaine);
  }
  const domaines: Partial<Record<string, ScoreDomaine>> = {};
  for (const { name } of DOMAINS_CONFIG) {
    if (!domainesRepondus.has(name)) continue;
    const totalBrut = totalBrutDomaine(name, scoreParEchelle);
    domaines[name] = { totalBrut, classe: classeDomaine(name, totalBrut) };
  }
  return domaines;
}

/** Moyenne et écart-type de population (n) sur les classes disponibles. */
function computeStats(values: number[]) {
  if (values.length === 0) return undefined;
  const moyenne = values.reduce((a, v) => a + v, 0) / values.length;
  const variance =
    values.reduce((a, v) => a + (v - moyenne) ** 2, 0) / values.length;
  return { moyenne, ecartType: Math.sqrt(variance), n: values.length };
}

/** Classes d'un domaine sur les lignes retenues, domaines absents exclus. */
function classesColonne(lignes: Copsoq3Ligne[], domaine: string): number[] {
  return lignes
    .map((ligne) => ligne.domaines[domaine]?.classe)
    .filter((classe): classe is number => classe !== undefined);
}

/** Seules les colonnes de domaine ont une moyenne : pas la Date. */
function estColonneDomaine(colonneId: string): boolean {
  return DOMAINS_CONFIG.some((d) => d.name === colonneId);
}

const colonnes: ColonneDataTable<Copsoq3Ligne>[] = [
  {
    id: "candidat",
    header: "Candidat",
    accessorFn: nomCandidat,
    cell: ({ row }) => {
      const nom = nomCandidat(row.original);
      return nom ? (
        <span className="font-medium whitespace-nowrap">{nom}</span>
      ) : (
        <span className="text-muted-foreground italic">Anonyme</span>
      );
    },
    enableHiding: false,
    filterFn: filtreRecherche<Copsoq3Ligne>((l) => [
      l.prenomCandidat,
      l.nomCandidat,
    ]),
    meta: { libelle: "Candidat", largeur: "12rem", figee: true },
  },
  {
    id: "date",
    header: "Date",
    accessorFn: (l) => l.dateFin ?? 0,
    cell: ({ row }) => (
      <span className="data whitespace-nowrap text-muted-foreground">
        {row.original.dateFin ? dateCourte(row.original.dateFin) : "—"}
      </span>
    ),
    meta: { libelle: "Date", largeur: "7rem" },
  },
  ...DOMAINS_CONFIG.map<ColonneDataTable<Copsoq3Ligne>>((domaine) => ({
    id: domaine.name,
    // `asChild` sur un <span> : l'en-tête triable est déjà un <button>, et un
    // bouton dans un bouton est du HTML invalide. Sur deux lignes plutôt que
    // tronqué : six intitulés tiennent en entier, « Organisation et M… » ne se
    // lit pas.
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default block whitespace-normal leading-tight">
            {domaine.name}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {domaine.name} — score étalonné de 1 à 5
        </TooltipContent>
      </Tooltip>
    ),
    // `null` plutôt qu'`undefined` pour un domaine absent : TanStack range les
    // valeurs nulles en fin de tri au lieu de les mêler aux classes.
    accessorFn: (l) => l.domaines[domaine.name]?.classe ?? null,
    cell: ({ row }) => {
      const score = row.original.domaines[domaine.name];
      return score === undefined ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span
          className={cn(
            "inline-block rounded-pastille border px-2 py-0.5 text-xs font-semibold tabular-nums",
            teinteClasse(score.classe),
          )}
          title={`Classe ${score.classe} sur 5 (score brut ${score.totalBrut})`}
        >
          {score.classe}/5
        </span>
      );
    },
    meta: { libelle: domaine.name, largeur: "7rem", auCentre: true },
  })),
];

/** Moyenne et écart-type, recalculés sur ce que les filtres ont retenu. */
const PIED: LignePied<Copsoq3Ligne>[] = [
  {
    libelle: (lignes) => {
      const nMax = Math.max(
        0,
        ...DOMAINS_CONFIG.map((d) => classesColonne(lignes, d.name).length),
      );
      return `Moyenne${nMax > 0 ? ` (n=${nMax})` : ""}`;
    },
    cellule: (colonneId, lignes) => {
      if (!estColonneDomaine(colonneId)) return null;
      const stats = computeStats(classesColonne(lignes, colonneId));
      return stats ? (
        <span className="font-semibold tabular-nums">
          {stats.moyenne.toFixed(1)}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  },
  {
    libelle: "Écart-type",
    cellule: (colonneId, lignes) => {
      if (!estColonneDomaine(colonneId)) return null;
      const stats = computeStats(classesColonne(lignes, colonneId));
      return (
        <span className="text-muted-foreground tabular-nums">
          {stats ? stats.ecartType.toFixed(1) : "—"}
        </span>
      );
    },
  },
];

/**
 * Décimale à la française : le CSV est séparé par `;` pour Excel FR, qui lirait
 * « 2.4 » comme du texte (voire une date).
 */
function decimale(valeur: number): string {
  return valeur.toFixed(1).replace(".", ",");
}

/**
 * Export : classe ET somme brute par domaine — la classe pour lire, le brut
 * pour recalculer dans un tableur. Ordre des données, pas celui de l'écran
 * (même choix que l'export FairView). Moyenne et écart-type en fin de fichier,
 * sur toutes les passations : l'export ignore la recherche de l'écran.
 */
function exporterCsv(campagneNom: string, lignes: Copsoq3Ligne[]) {
  const entetes = [
    "Candidat",
    "Date",
    ...DOMAINS_CONFIG.flatMap((d) => [
      `${d.name} (classe /5)`,
      `${d.name} (score brut)`,
    ]),
  ];
  const rangees = lignes.map((ligne) => [
    nomCandidat(ligne) || "Anonyme",
    ligne.dateFin ? dateCourte(ligne.dateFin) : "",
    ...DOMAINS_CONFIG.flatMap((d) => {
      const score = ligne.domaines[d.name];
      return score
        ? [String(score.classe), String(score.totalBrut)]
        : ["", ""];
    }),
  ]);

  const stats = DOMAINS_CONFIG.map((d) => {
    const scores = lignes.flatMap((l) => l.domaines[d.name] ?? []);
    return {
      classe: computeStats(scores.map((sc) => sc.classe)),
      brut: computeStats(scores.map((sc) => sc.totalBrut)),
    };
  });
  const nMax = Math.max(0, ...stats.map((st) => st.classe?.n ?? 0));
  const ligneStat = (
    libelle: string,
    valeur: (st: NonNullable<ReturnType<typeof computeStats>>) => number,
  ) => [
    libelle,
    "",
    ...stats.flatMap((st) => [
      st.classe ? decimale(valeur(st.classe)) : "",
      st.brut ? decimale(valeur(st.brut)) : "",
    ]),
  ];
  rangees.push(
    ligneStat(`Moyenne (n=${nMax})`, (st) => st.moyenne),
    ligneStat("Écart-type", (st) => st.ecartType),
  );

  telechargerCsv(campagneNom, entetes, rangees);
}

export function Copsoq3CampagneTable({ evaluations, campagneNom }: Props) {
  // Tri interne : la table n'a pas d'URL à elle, et l'ordre par défaut
  // (dernières passations d'abord) doit survivre au tri manuel de l'utilisateur.
  const [tri, setTri] = useState<SortingState>(TRI_INITIAL);

  const lignes = useMemo<Copsoq3Ligne[]>(
    () =>
      evaluations.map((evaluation) => ({
        ...evaluation,
        domaines: scoresDomaines(evaluation),
      })),
    [evaluations],
  );

  if (evaluations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucune passation COPSOQ III terminée dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {pluriel(evaluations.length, "passation")}{" "}
          {motPluriel(evaluations.length, "terminée")}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => campagneNom && exporterCsv(campagneNom, lignes)}
          disabled={!campagneNom}
        >
          <Download className="h-4 w-4 mr-2" />
          Exporter CSV
        </Button>
      </div>
      <DataTable
        colonnes={colonnes}
        donnees={lignes}
        cleColonnes="campagne-copsoq3"
        barreFiltres={BARRE_FILTRES}
        tri={tri}
        onTriChange={setTri}
        // Une moyenne calculée sur une page de dix candidats ne serait la
        // moyenne de rien.
        taillePage="toutes"
        largeurColonnes="auto"
        densite="compacte"
        pied={PIED}
        videFiltre={{ titre: "Aucun candidat pour cette recherche" }}
      />
    </>
  );
}
